import "./App.css";
import "./i18n";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PUBLIC_LANGS, stripLangPrefix, getLangPrefix, isPublicLocalizedPath } from "./lib/langRoutes";
import { isCrawler, countryHint } from "./lib/langDetect";
import { canonicalizeSeoSlug, seoSlugFor, canonicalizeCapSlug, capSlugFor, CAP_SEGMENTS_ALL } from "./lib/localizedSlugs";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import BlogIndexPage from "./pages/BlogIndexPage";
import BlogArticlePage from "./pages/BlogArticlePage";
import SeoLandingPage, { SEO_SLUGS } from "./pages/SeoLandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import NewQuotePage from "./pages/NewQuotePage";
import QuoteDetailPage from "./pages/QuoteDetailPage";
import MyMachinesPage from "./pages/MyMachinesPage";
import CustomersPage from "./pages/CustomersPage";
import ImportTimeStudyPage from "./pages/ImportTimeStudyPage";
import CalibrationPage from "./pages/CalibrationPage";
import CostAnalyticsPage from "./pages/CostAnalyticsPage";
import MarketplacePage from "./pages/MarketplacePage";
import MachineRoiPage from "./pages/MachineRoiPage";
import BillingPage from "./pages/BillingPage";
import BillingSuccessPage from "./pages/BillingSuccessPage";
import AdminPage from "./pages/AdminPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import UnsubscribePage from "./pages/UnsubscribePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LostOpportunityPage from "./pages/LostOpportunityPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import SettingsPage from "./pages/SettingsPage";
import CapabilityPage from "./pages/CapabilityPage";
import CopilotWidget from "./components/app/CopilotWidget";
import CookieConsent from "./components/app/CookieConsent";
import AnalysisJobWatcher from "./components/app/AnalysisJobWatcher";
import { Toaster } from "./components/ui/sonner";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { initPostHog, capturePageview, identifyUser, resetIdentity, isAnalyticsAllowed, setAnalyticsLanguage, setAnalyticsCountry, markInternalUser } from "./lib/analytics";

function AnalyticsBridge() {
  const { user } = useAuth();
  const location = useLocation();
  const { i18n } = useTranslation();

  // Boot PostHog after first paint (only if consent + key present)
  useEffect(() => {
    initPostHog();
    setAnalyticsCountry(countryHint());
    const onConsent = () => initPostHog();
    window.addEventListener("analytics-consent-changed", onConsent);
    return () => window.removeEventListener("analytics-consent-changed", onConsent);
  }, []);

  // Identify on login AND on every session restored from the auth cookie,
  // otherwise returning users stay anonymous for the whole session.
  useEffect(() => {
    if (user === false) resetIdentity();      // logout: stop attributing events
    if (!isAnalyticsAllowed()) return;
    if (user && user.id) identifyUser(user);
  }, [user]);

  // Super properties attached to every event (incl. autocapture).
  useEffect(() => {
    setAnalyticsLanguage((i18n.language || "").split("-")[0]);
  }, [i18n.language]);

  // Internal traffic: admin accounts, the admin console and anything arriving
  // from the Emergent workspace must not pollute product funnels.
  useEffect(() => {
    if (user?.role === "admin") markInternalUser("admin_account");
    else if (location.pathname.startsWith("/admin")) markInternalUser("admin_path");
    else if (/(^|\.)app\.emergent\.sh$/i.test(((() => { try { return new URL(document.referrer).hostname; } catch { return ""; } })()))) {
      markInternalUser("emergent_referrer");
    }
  }, [user, location.pathname]);

  // Pageview on route change
  useEffect(() => {
    capturePageview(location.pathname);
  }, [location.pathname]);

  return null;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === null) return null; // still checking
  if (user && typeof user === "object") return <Navigate to="/dashboard" replace/>;
  return <LandingPage/>;
}

/* Sets the i18n language from the /:lang/ URL prefix and renders public pages.
   L'italiano è canonico alla ROOT: /it/* reindirizza sempre al path senza prefisso. */
function LangWrapper() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();
  const valid = PUBLIC_LANGS.includes(lang);
  useEffect(() => {
    if (valid && lang !== "it" && (i18n.language || "").split("-")[0] !== lang) i18n.changeLanguage(lang);
  }, [lang, valid, i18n]);
  if (lang === "it") {
    const base = stripLangPrefix(location.pathname) || "/";
    return <Navigate to={`${base}${location.search}${location.hash}`} replace />;
  }
  if (!valid) return <Navigate to="/" replace />;
  return <Outlet />;
}

/* Legacy x-default capability URL (/capabilities/:slug) → canonico italiano
   alla root (/funzionalita/{slug-it}). */
function CapabilitiesLegacyRedirect() {
  const { slug } = useParams();
  const canonical = canonicalizeCapSlug(slug);
  if (!canonical) return <Navigate to="/" replace />;
  return <Navigate to={`/funzionalita/${capSlugFor(canonical, "it")}`} replace />;
}

/* Italiano canonico alla root: sulle pagine pubbliche SENZA prefisso lingua
   l'i18n deve essere 'it' (crawler e visitatori anonimi), altrimenti il
   contenuto resta nell'ultima lingua usata e <html lang> non corrisponde
   all'URL canonica. Gli utenti loggati mantengono la loro scelta.
   Eccezione: home "/" e "/register" seguono l'Accept-Language del visitatore
   (vedi lib/langDetect) quando non c'è una preferenza salvata e non si tratta
   di un crawler — la lingua scelta sopravvive al passaggio dalla CTA. */
const AUTO_LANG_PATHS = new Set(["/", "/register"]);

function ItCanonicalLangGuard() {
  const location = useLocation();
  const { i18n } = useTranslation();
  const { user } = useAuth();
  useEffect(() => {
    if (user && typeof user === "object") return;
    if (AUTO_LANG_PATHS.has(location.pathname) && !isCrawler()) return;
    const prefix = getLangPrefix(location.pathname);
    if (!prefix && isPublicLocalizedPath(location.pathname) && (i18n.language || "").split("-")[0] !== "it") {
      i18n.changeLanguage("it");
    }
  }, [location.pathname, i18n, user]);
  return null;
}

/* Legacy ?lang=xx → URL canonica path-based (it = root, altre /{lang}/...). */
function LangParamRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const lang = (params.get("lang") || "").toLowerCase().slice(0, 2);
    if (!lang || !PUBLIC_LANGS.includes(lang)) return;
    params.delete("lang");
    const rest = params.toString();
    const base = stripLangPrefix(location.pathname) || "/";
    const target = lang === "it" ? base : (base === "/" ? `/${lang}/` : `/${lang}${base}`);
    navigate(`${target}${rest ? `?${rest}` : ""}${location.hash}`, { replace: true });
  }, [location, navigate]);
  return null;
}

/* Resolves a language-local SEO slug (e.g. /de/cnc-angebotssoftware) to its
   canonical page, redirecting legacy/wrong-language slugs to the local one. */
function LangSeoRoute() {
  const { lang, slug } = useParams();
  if (CAP_SEGMENTS_ALL.includes(slug)) return <Navigate to={`/${lang}/#capabilities`} replace />;
  const canonical = canonicalizeSeoSlug(slug);
  if (!canonical) return <Navigate to={`/${lang}/`} replace />;
  const expected = seoSlugFor(canonical, lang);
  if (slug !== expected) return <Navigate to={`/${lang}/${expected}`} replace />;
  return <SeoLandingPage slug={canonical} />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <LangParamRedirect />
            <ItCanonicalLangGuard />
            <Routes>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/blog" element={<BlogIndexPage />} />
              <Route path="/blog/:slug" element={<BlogArticlePage />} />
              {SEO_SLUGS.map((s) => (
                <Route key={s} path={`/${s}`} element={<SeoLandingPage slug={s} />} />
              ))}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/cookies" element={<CookiePolicyPage />} />
              <Route path="/capabilities/:slug" element={<CapabilitiesLegacyRedirect />} />
              <Route path="/funzionalita/:slug" element={<CapabilityPage />} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/new-quote" element={<ProtectedRoute><NewQuotePage /></ProtectedRoute>} />
              <Route path="/import-time-study" element={<ProtectedRoute><ImportTimeStudyPage /></ProtectedRoute>} />
              <Route path="/quotes/:id" element={<ProtectedRoute><QuoteDetailPage /></ProtectedRoute>} />
              <Route path="/machines" element={<ProtectedRoute><MyMachinesPage /></ProtectedRoute>} />
              <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
              <Route path="/calibration" element={<ProtectedRoute><CalibrationPage /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute><CostAnalyticsPage /></ProtectedRoute>} />
              <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
              <Route path="/guardrail" element={<Navigate to="/dashboard" replace />} />
              <Route path="/machine-roi" element={<ProtectedRoute><MachineRoiPage /></ProtectedRoute>} />
              <Route path="/lost-opportunity" element={<ProtectedRoute><LostOpportunityPage /></ProtectedRoute>} />
              <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
              <Route path="/billing/success" element={<ProtectedRoute><BillingSuccessPage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
              {/* Language-prefixed public pages: /it/, /en/blog, /de/software-preventivi-cnc, ... */}
              <Route path="/:lang" element={<LangWrapper />}>
                <Route index element={<LandingPage />} />
                <Route path="blog" element={<BlogIndexPage />} />
                <Route path="blog/:slug" element={<BlogArticlePage />} />
                {CAP_SEGMENTS_ALL.map((seg) => (
                  <Route key={`cap-${seg}`} path={`${seg}/:slug`} element={<CapabilityPage />} />
                ))}
                <Route path="privacy" element={<PrivacyPolicyPage />} />
                <Route path="terms" element={<TermsPage />} />
                <Route path="cookies" element={<CookiePolicyPage />} />
                <Route path=":slug" element={<LangSeoRoute />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <CopilotWidget />
            <CookieConsent />
            <AnalyticsBridge />
            <AnalysisJobWatcher />
            <Toaster position="bottom-right" offset={{ bottom: 150, right: 24 }} mobileOffset={{ bottom: 150 }} richColors closeButton />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
